import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StickyNote } from 'lucide-react';
import apiClient from '../config/axios';
import { useAuth } from '../context/AuthContext';

/**
 * Personal note on an entry (issue 193): one private plain-text note per
 * (user, system, entity address), the same store issue 192's per-favorite
 * note reads. Page CONTENT, not an action-row control (#287 is open about
 * that row) — it renders after the detail sections, collapsed to a single
 * "Add a personal note" affordance until the user has written something.
 *
 * SAVE ON BLUR, deliberately: one PUT per editing session instead of one per
 * keystroke, which matters under the 100 req/min per-IP limiter. Blur with
 * unchanged text sends nothing; blur with the text cleared DELETEs the note
 * (an emptied note is a deletion, and the server's PUT refuses empty bodies).
 *
 * PLAIN TEXT ALL THE WAY OUT. The note lives in a <textarea> value — never
 * dangerouslySetInnerHTML, never RichText — so markup a user types (or was
 * tricked into pasting) is inert characters, not DOM. The server stores it
 * verbatim under the same contract.
 *
 * The component never consults the Family registry: the entity address is
 * whatever the detail page passes, so a Family the bundle has never seen — a
 * user-authored system's, a homebrew layer's — is annotatable by the same
 * code path. The server is the authority on what resolves.
 *
 * Guests render nothing (a note needs an account to belong to).
 */
export default function EntryNotePanel({ entityType, slug }) {
  const { isAuthenticated } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState('');
  // The server's copy, or null when no note exists — what blur diffs against.
  const [savedBody, setSavedBody] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const textareaRef = useRef(null);
  const focusOnExpand = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !slug) return undefined;
    let cancelled = false;
    setLoaded(false);
    setExpanded(false);
    setBody('');
    setSavedBody(null);
    setStatus('idle');
    apiClient
      .get(`/api/notes?entity_type=${encodeURIComponent(entityType)}&slug=${encodeURIComponent(slug)}`)
      .then((res) => {
        if (cancelled) return;
        // 200 { note: null } is the ordinary "no note yet" (also, identically,
        // another account's note — the server keeps the two indistinguishable);
        // a real note arrives as { note: { body, ... } }.
        const note = res.data && res.data.note;
        if (note && typeof note.body === 'string') {
          setBody(note.body);
          setSavedBody(note.body);
          setExpanded(true);
        }
        setLoaded(true);
      })
      .catch(() => {
        // Any failure degrades the same way as "no note yet" — the collapsed
        // affordance, never a broken page.
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, entityType, slug]);

  useEffect(() => {
    if (expanded && focusOnExpand.current && textareaRef.current) {
      focusOnExpand.current = false;
      textareaRef.current.focus();
    }
  }, [expanded]);

  const handleBlur = useCallback(async () => {
    const trimmed = body.trim();
    if (trimmed === (savedBody ?? '')) {
      // Nothing changed (or nothing was ever written): no request. An empty
      // never-saved note just collapses back to the affordance.
      if (!trimmed && savedBody === null) setExpanded(false);
      return;
    }
    setStatus('saving');
    try {
      if (!trimmed) {
        await apiClient.delete('/api/notes', {
          data: { entity_type: entityType, slug },
        });
        setSavedBody(null);
        setBody('');
        setExpanded(false);
        setStatus('idle');
      } else {
        const res = await apiClient.put('/api/notes', {
          entity_type: entityType,
          slug,
          body: trimmed,
        });
        setSavedBody(res.data.body);
        setStatus('saved');
      }
    } catch (err) {
      setStatus('error');
    }
  }, [body, savedBody, entityType, slug]);

  if (!isAuthenticated || !slug || !loaded) return null;

  if (!expanded) {
    return (
      <button
        type="button"
        className="btn btn-secondary inline-flex items-center gap-2"
        onClick={() => {
          focusOnExpand.current = true;
          setExpanded(true);
        }}
      >
        <StickyNote className="w-4 h-4" aria-hidden="true" />
        <span>Add a personal note</span>
      </button>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="stat-block-section inline-flex items-center gap-2">
          <StickyNote className="w-4 h-4" aria-hidden="true" />
          <span>Your note</span>
        </h2>
        <span className="text-sm text-muted" aria-live="polite">
          {status === 'saving' && 'Saving…'}
          {status === 'saved' && 'Saved'}
          {status === 'error' && 'Could not save — your text is still here'}
        </span>
      </div>
      <textarea
        ref={textareaRef}
        className="input w-full"
        rows={4}
        aria-label="Your personal note"
        placeholder="Only you can see this. Saved when you click away; clear it to delete."
        maxLength={10000}
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          if (status === 'saved' || status === 'error') setStatus('idle');
        }}
        onBlur={handleBlur}
      />
      <p className="text-xs text-muted mt-1">
        Private to your account. Plain text, saved on click-away; clear the box to delete the note.
      </p>
    </div>
  );
}
